import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { readPref } from '../services/crossShellPrefs';
import { LAST_SHELL_KEY } from '../shell/houseLogin';
import { follow, settleLogin } from '../shell/postLogin';
import { withResume } from '../shell/lastSpot';
import { classifyLoginError, refusalForNotice, type LoginRefusal } from './loginErrors';
import BetaBadge from '../components/BetaBadge';
import Logo from '../components/Logo';
import LegalLinks from '../components/LegalLinks';
import GoogleSignInButton from '../components/GoogleSignInButton';

interface LocationState {
  from?: {
    pathname: string;
    search?: string;
  };
  message?: string;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const state = location.state as LocationState;
  // WHERE TO GO BACK TO: the page that sent us here, address and all; with
  // none, the front page marked to resume the last spot (`lastSpot`).
  const from = state?.from ? `${state.from.pathname}${state.from.search ?? ''}` : withResume('/');
  const successMessage = state?.message;
  // A refused sign-in, sorted into its box: red for a credential failure,
  // amber for "the account isn't ready", amber-with-a-button for "you never
  // clicked the link". Seeded from `?notice=` when the Google callback sent
  // us here — it has no response body to say it in.
  const [refusal, setRefusal] = useState<LoginRefusal | null>(() =>
    refusalForNotice(params.get('notice')),
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRefusal(null);
    setResent('');
    setLoading(true);

    try {
      await login(email, password);
      // WHERE TO GO IS DECIDED HERE, not by the router. A product host sent us
      // `?next=`; failing that, the shell this browser last worked in is
      // remembered in a shared cookie. Either is followed only when the
      // product is HELD — otherwise the house, where the launcher shows it
      // locked. See shell/postLogin.ts; the Google done-page runs the same.
      const dest = await settleLogin({
        getBilling: () => api.getBilling(),
        next: params.get('next'),
        lastShell: readPref(LAST_SHELL_KEY),
        hostname: window.location.hostname,
        origin: window.location.origin,
        fallback: from,
      });
      // Stay "Signing in..." until the new page paints. Not in a `finally`:
      // a `return` inside `try` still runs it, which re-enabled the button
      // mid-redirect and let a second click send a second login.
      if (!follow(dest, navigate)) setLoading(false);
    } catch (err) {
      setRefusal(classifyLoginError(err));
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResent('');
    try {
      const { message } = await api.resendVerification(email);
      setResent(message);
    } catch (err) {
      setResent(err instanceof Error ? err.message : 'Could not resend.');
    }
  };

  const notice = refusal && refusal.kind !== 'error' ? refusal.message : '';
  const error = refusal && refusal.kind === 'error' ? refusal.message : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Logo size={56} />
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
            Lumnian
            <BetaBadge />
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {successMessage && (
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded">
              {successMessage}
            </div>
          )}

          {notice && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-4 py-3 rounded">
              {notice}
              {refusal?.kind === 'unverified' && (
                <div className="mt-2">
                  {resent ? (
                    <span className="text-sm">{resent}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={!email}
                      className="text-sm font-medium underline hover:no-underline disabled:opacity-50"
                    >
                      Resend verification email
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            {/* `name` + `autocomplete`: what password managers key on. Without
                them the browser has to guess from the id, and it guesses less
                readily on an http dev host than it did on localhost. */}
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <Link to="/forgot-password" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500">
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <GoogleSignInButton next={params.get('next')} />

          <p className="text-center text-sm text-gray-600 dark:text-gray-300">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:text-blue-500">
              Create one
            </Link>
          </p>

          <LegalLinks />
        </form>
      </div>
    </div>
  );
}
